import apiClient from './client';

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AuthTokens {
  access: string;
  refresh: string;
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  default_instrument: string | null;
  avatar_url: string | null;
  avatar_type: 'UPLOAD' | 'PRESET';
}

export interface AvatarResponse {
  avatar_url: string;
  avatar_type: 'UPLOAD' | 'PRESET';
}

export interface PresetAvatar {
  name: string;
  url: string;
}

export interface LoginResponse extends AuthTokens {
  user: UserProfile;
}

export const authApi = {
  register: (data: RegisterData) =>
    apiClient.post<{ id: string; name: string; email: string; is_email_verified: boolean; message: string }>(
      '/auth/register/',
      data
    ),

  login: (data: LoginData) =>
    apiClient.post<LoginResponse>('/auth/login/', data),

  googleLogin: (idToken: string) =>
    apiClient.post<LoginResponse>('/auth/google/', { id_token: idToken }),

  verifyEmail: (token: string) =>
    apiClient.post<{ message: string }>('/auth/verify-email/', { token }),

  refreshToken: (refresh: string) =>
    apiClient.post<AuthTokens>('/auth/token/refresh/', { refresh }),

  resetPasswordRequest: (email: string) =>
    apiClient.post<{ message: string }>('/auth/password-reset/request/', { email }),

  resetPasswordConfirm: (token: string, password: string) =>
    apiClient.post<{ message: string }>('/auth/password-reset/confirm/', { token, password }),

  getProfile: () =>
    apiClient.get<UserProfile>('/users/me/'),

  updateProfile: (data: Partial<Pick<UserProfile, 'name' | 'default_instrument'>>) =>
    apiClient.patch<UserProfile>('/users/me/', data),

  uploadAvatar: (file: File) => {
    const form = new FormData();
    form.append('avatar', file);
    return apiClient.post<AvatarResponse>('/users/me/avatar/upload/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  selectPresetAvatar: (preset_name: string) =>
    apiClient.post<AvatarResponse>('/users/me/avatar/preset/', { preset_name }),

  listPresetAvatars: () =>
    apiClient.get<{ presets: PresetAvatar[] }>('/users/me/avatar/presets/'),
};
